import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { integrationCategoriesApi } from '../../services/adminApi';

const IntegrationCategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    sort_order: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
  }, [id]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const response = await integrationCategoriesApi.getById(id);
      if (response.data.success) {
        const category = response.data.data;
        setFormData({
          title: category.title || '',
          description: category.description || '',
          icon: category.icon || '',
          sort_order: category.sort_order || 0,
          is_active: category.is_active !== undefined ? category.is_active : true
        });
      } else {
        setError('Kategori bulunamadı');
      }
    } catch (err) {
      console.error('Category fetch error:', err);
      setError('Kategori yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setError('Kategori başlığı gereklidir');
      return;
    }
    if (!formData.description.trim()) {
      setError('Kategori açıklaması gereklidir');
      return;
    }
    if (!formData.icon.trim()) {
      setError('İkon gereklidir');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let response;
      if (isEdit) {
        response = await integrationCategoriesApi.update(id, formData);
      } else {
        response = await integrationCategoriesApi.create(formData);
      }

      if (response.data.success) {
        alert(isEdit ? 'Kategori başarıyla güncellendi!' : 'Kategori başarıyla eklendi!');
        navigate('/admin/integration-categories');
      } else {
        setError(response.data.message || 'Bir hata oluştu');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const formContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    maxWidth: '48rem',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '2rem',
    textAlign: 'center'
  };

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    lineHeight: '1.5',
    color: '#111827',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    boxSizing: 'border-box'
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '6rem',
    resize: 'vertical'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '2rem'
  };

  const checkboxStyle = {
    marginRight: '0.75rem',
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer'
  };

  const checkboxLabelStyle = {
    ...labelStyle,
    marginBottom: 0,
    cursor: 'pointer'
  };

  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem'
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease-in-out',
    border: 'none',
    minWidth: '7rem'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db'
  };

  const submitButtonStyle = {
    ...buttonStyle,
    backgroundColor: loading ? '#9ca3af' : '#3b82f6',
    color: 'white',
    border: 'none'
  };

  const errorStyle = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    marginBottom: '1.5rem'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '16rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  if (loading && !isEdit) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Entegrasyon Kategorisi Düzenle' : 'Yeni Entegrasyon Kategorisi Ekle'} - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={formContainerStyle}>
          <h1 style={titleStyle}>
            {isEdit ? 'Entegrasyon Kategorisi Düzenle' : 'Yeni Entegrasyon Kategorisi Ekle'}
          </h1>

          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label htmlFor="title" style={labelStyle}>
                Kategori Başlığı *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Örn: Ödeme Sistemleri"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="description" style={labelStyle}>
                Kategori Açıklaması *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                style={textareaStyle}
                placeholder="Kategori hakkında detaylı açıklama..."
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="icon" style={labelStyle}>
                İkon *
              </label>
              <input
                type="text"
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Örn: 🔌"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="sort_order" style={labelStyle}>
                Sıralama
              </label>
              <input
                type="number"
                id="sort_order"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                style={inputStyle}
                placeholder="0"
                min="0"
              />
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={checkboxStyle}
              />
              <label htmlFor="is_active" style={checkboxLabelStyle}>
                Aktif
              </label>
            </div>

            <div style={buttonContainerStyle}>
              <button
                type="button"
                onClick={() => navigate('/admin/integration-categories')}
                style={cancelButtonStyle}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : (isEdit ? 'Güncelle' : 'Ekle')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default IntegrationCategoryForm;
